import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CalendarIcon, DownloadIcon, ClockIcon, TagIcon, ArrowRightIcon } from 'lucide-react';
export function InsightHub() {
  const blogPosts = [{
    title: 'How to Measure Retreat ROI',
    excerpt: 'Learn practical methods to quantify the return on investment from your corporate retreats beyond just team satisfaction.',
    date: 'October 15, 2023',
    readTime: '8 min read',
    category: 'Strategy',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Cancun vs. Miami: MICE Showdown',
    excerpt: 'A detailed comparison of hosting your next corporate event in Cancun versus Miami, from budget considerations to unique experiences.',
    date: 'September 28, 2023',
    readTime: '10 min read',
    category: 'Destinations',
    image: 'https://images.unsplash.com/photo-1602088113235-229c19758e9f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Cancun vs. Vegas: The Ultimate Corporate Destination Battle',
    excerpt: 'Which destination offers better value, experiences, and outcomes for your next corporate event? We break down the pros and cons.',
    date: 'August 17, 2023',
    readTime: '12 min read',
    category: 'Destinations',
    image: 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80'
  }, {
    title: 'The 2025 Corporate Retreat Budget Guide',
    excerpt: 'Plan ahead with our comprehensive guide to budgeting for corporate retreats in 2025, including emerging trends and cost-saving strategies.',
    date: 'November 5, 2023',
    readTime: '15 min read',
    category: 'Planning',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2011&q=80'
  }];
  const resources = [{
    title: 'Corporate Retreat Budget Calculator',
    type: 'Calculator',
    description: 'Interactive tool to estimate costs based on group size, duration, and activities.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2011&q=80'
  }, {
    title: 'Event Planning Checklist',
    type: 'PDF Guide',
    description: 'Comprehensive checklist covering all aspects of planning a successful corporate event.',
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'ROI Measurement Framework',
    type: 'Template',
    description: 'Excel template for tracking and measuring the ROI of your corporate retreats.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2369">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-2370">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2371">
          <div className="max-w-3xl" data-id="element-2372">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2373">
              Insight Hub
            </h1>
            <p className="text-xl text-white/90" data-id="element-2374">
              Expert resources to help you plan and maximize the impact of your
              corporate events.
            </p>
          </div>
        </div>
      </section>
      {/* Webinar Banner */}
      <section className="py-8 bg-[#FF6B65]" data-id="element-2375">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2376">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-2377">
            <div className="text-white" data-id="element-2378">
              <h2 className="text-2xl font-bold mb-2" data-id="element-2379">
                Retreat ROI Live Webinar
              </h2>
              <p data-id="element-2380">
                Join our experts on November 6, 2025 to learn how to measure and
                maximize your retreat ROI.
              </p>
            </div>
            <Button variant="primary" className="bg-[#374047] text-white hover:bg-[#374047]/90" data-id="element-2381">
              <CalendarIcon className="h-5 w-5 mr-2" data-id="element-2382" />
              Register Now
            </Button>
          </div>
        </div>
      </section>
      {/* Blog Posts */}
      <section className="py-16 bg-white" data-id="element-2383">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2384">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2385">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2386">
              Latest Insights
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2387">
              Expert articles to help you plan and execute exceptional corporate
              events.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2388">
            {blogPosts.map((post, index) => <Card key={index} className="h-full overflow-hidden" data-id="element-2389">
                <div className="h-48 overflow-hidden" data-id="element-2390">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-2391" />
                </div>
                <CardHeader data-id="element-2392">
                  <div className="flex justify-between items-center mb-2" data-id="element-2393">
                    <div className="flex items-center" data-id="element-2394">
                      <CalendarIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-2395" />
                      <span className="text-xs text-[#374047]/60" data-id="element-2396">
                        {post.date}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-2397">
                      <ClockIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-2398" />
                      <span className="text-xs text-[#374047]/60" data-id="element-2399">
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1] mb-2" data-id="element-2400">
                    <TagIcon className="h-3 w-3 mr-1" data-id="element-2401" />
                    {post.category}
                  </span>
                  <h3 className="text-xl font-bold text-[#374047]" data-id="element-2402">
                    {post.title}
                  </h3>
                </CardHeader>
                <CardBody data-id="element-2403">
                  <p className="text-[#374047]/80" data-id="element-2404">{post.excerpt}</p>
                </CardBody>
                <CardFooter data-id="element-2405">
                  <Button variant="text" className="text-[#0099A1] hover:text-[#0099A1]/80" data-id="element-2406">
                    Read More
                    <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-2407" />
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>
      </section>
      {/* Resources */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-2408">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2409">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2410">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2411">
              Free Resources
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2412">
              Download our most popular planning tools and templates.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2413">
            {resources.map((resource, index) => <Card key={index} className="h-full" data-id="element-2414">
                <div className="h-40 overflow-hidden" data-id="element-2415">
                  <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" data-id="element-2416" />
                </div>
                <CardHeader data-id="element-2417">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1] mb-2" data-id="element-2418">
                    {resource.type}
                  </span>
                  <h3 className="text-lg font-bold text-[#374047]" data-id="element-2419">
                    {resource.title}
                  </h3>
                </CardHeader>
                <CardBody data-id="element-2420">
                  <p className="text-sm text-[#374047]/80" data-id="element-2421">
                    {resource.description}
                  </p>
                </CardBody>
                <CardFooter data-id="element-2422">
                  <Button variant="outline" className="w-full flex items-center justify-center" data-id="element-2423">
                    <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2424" />
                    Download
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>
      </section>
      <CtaSection data-id="element-2425" />
    </div>;
}